import React from 'react';
import { Card } from '../../components/ui/Card';
import { TabNavigation } from '../../components/operations/TabNavigation';
import { BarChart, Bar, XAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Eye, MessageSquare, CheckCircle, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
const PROCEDURE_TABS = [{
  label: 'Library',
  path: ''
}, {
  label: 'AI Agents',
  path: 'ai-agents'
}, {
  label: 'Onboarding',
  path: 'onboarding'
}, {
  label: 'Analytics',
  path: 'analytics'
}];
const HOURLY_DATA = [{
  hour: '10',
  chats: 12
}, {
  hour: '11',
  chats: 25
}, {
  hour: '12',
  chats: 45
}, {
  hour: '13',
  chats: 65
}, {
  hour: '14',
  chats: 58
}, {
  hour: '15',
  chats: 42
}, {
  hour: '16',
  chats: 28
}, {
  hour: '17',
  chats: 35
}, {
  hour: '18',
  chats: 48
}, {
  hour: '19',
  chats: 55
}, {
  hour: '20',
  chats: 62
}, {
  hour: '21',
  chats: 45
}, {
  hour: '22',
  chats: 25
}];
export function Analytics() {
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-2889">
      <h1 className="text-2xl font-bold text-text-primary" data-id="element-2890">Analytics</h1>
      <TabNavigation tabs={PROCEDURE_TABS} basePath="/procedures" data-id="element-2891" />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2892">
        <Card className="p-4" data-id="element-2893">
          <div className="flex items-center gap-2 text-sm text-text-secondary mb-1" data-id="element-2894">
            <Eye size={16} data-id="element-2895" /> Doc Views
          </div>
          <div className="text-2xl font-bold mb-1" data-id="element-2896">847</div>
          <div className="text-xs text-accent-success" data-id="element-2897">+12% vs LW</div>
        </Card>
        <Card className="p-4" data-id="element-2898">
          <div className="flex items-center gap-2 text-sm text-text-secondary mb-1" data-id="element-2899">
            <MessageSquare size={16} data-id="element-2900" /> AI Conversations
          </div>
          <div className="text-2xl font-bold mb-1" data-id="element-2901">234</div>
          <div className="text-xs text-accent-success" data-id="element-2902">+8% vs LW</div>
        </Card>
        <Card className="p-4" data-id="element-2903">
          <div className="flex items-center gap-2 text-sm text-text-secondary mb-1" data-id="element-2904">
            <CheckCircle size={16} data-id="element-2905" /> Resolved by AI
          </div>
          <div className="text-2xl font-bold mb-1" data-id="element-2906">89%</div>
          <div className="text-xs text-accent-success" data-id="element-2907">+2% vs LW</div>
        </Card>
        <Card className="p-4" data-id="element-2908">
          <div className="flex items-center gap-2 text-sm text-text-secondary mb-1" data-id="element-2909">
            <AlertTriangle size={16} data-id="element-2910" /> Escalated
          </div>
          <div className="text-2xl font-bold mb-1" data-id="element-2911">26 (11%)</div>
          <div className="text-xs text-accent-success" data-id="element-2912">-1% vs LW</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2913">
        {/* AI Agent Performance */}
        <Card noPadding data-id="element-2914">
          <div className="p-4 border-b border-white/10 font-bold" data-id="element-2915">
            AI Agent Performance
          </div>
          <table className="w-full text-sm" data-id="element-2916">
            <thead data-id="element-2917">
              <tr className="text-text-secondary border-b border-white/5" data-id="element-2918">
                <th className="text-left p-4" data-id="element-2919">AGENT</th>
                <th className="text-center p-4" data-id="element-2920">CHATS</th>
                <th className="text-center p-4" data-id="element-2921">RESOLVED</th>
                <th className="text-center p-4" data-id="element-2922">SATISFACTION</th>
              </tr>
            </thead>
            <tbody data-id="element-2923">
              <tr className="border-b border-white/5 hover:bg-white/5" data-id="element-2924">
                <td className="p-4 font-bold" data-id="element-2925">🤖 Troubleshooter</td>
                <td className="p-4 text-center" data-id="element-2926">187</td>
                <td className="p-4 text-center text-accent-success font-bold" data-id="element-2927">
                  91%
                </td>
                <td className="p-4 text-center" data-id="element-2928">4.6/5 ⭐</td>
              </tr>
              <tr className="hover:bg-white/5" data-id="element-2929">
                <td className="p-4 font-bold" data-id="element-2930">🎓 Onboarding</td>
                <td className="p-4 text-center" data-id="element-2931">47</td>
                <td className="p-4 text-center text-accent-warning font-bold" data-id="element-2932">
                  85%
                </td>
                <td className="p-4 text-center" data-id="element-2933">4.8/5 ⭐</td>
              </tr>
            </tbody>
          </table>
        </Card>

        {/* Most Viewed Documents */}
        <Card className="p-6" data-id="element-2934">
          <h3 className="font-bold mb-4 flex items-center gap-2" data-id="element-2935">
            <Eye size={20} className="text-accent-primary" data-id="element-2936" /> Most Viewed
            Documents
          </h3>
          <div className="space-y-4" data-id="element-2937">
            {[{
            name: 'Morning Opening Checklist',
            views: 312
          }, {
            name: 'Night Closing Checklist',
            views: 298
          }, {
            name: 'Food Safety & Hygiene',
            views: 234
          }, {
            name: 'Grill Troubleshooting',
            views: 156
          }].map((doc, i) => <div key={i} data-id="element-2938">
                <div className="flex justify-between text-sm mb-1" data-id="element-2939">
                  <span className="font-medium" data-id="element-2940">{doc.name}</span>
                  <span className="text-text-secondary" data-id="element-2941">{doc.views} views</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden" data-id="element-2942">
                  <div className="h-full bg-accent-primary" style={{
                width: `${doc.views / 312 * 100}%`
              }} data-id="element-2943"></div>
                </div>
              </div>)}
          </div>
        </Card>

        {/* Top AI Questions */}
        <Card className="p-6" data-id="element-2944">
          <h3 className="font-bold mb-4 flex items-center gap-2" data-id="element-2945">
            <MessageSquare size={20} className="text-accent-success" data-id="element-2946" /> Top AI
            Questions
          </h3>
          <div className="space-y-3" data-id="element-2947">
            {[{
            q: '"Gril ne greje"',
            count: 23
          }, {
            q: '"Kupac traži refund"',
            count: 18
          }, {
            q: '"Kako se pravi Triple Smash"',
            count: 15
          }].map((item, i) => <div key={i} className="flex justify-between items-center p-3 bg-white/5 rounded-lg" data-id="element-2948">
                <span className="italic text-sm" data-id="element-2949">"{item.q}"</span>
                <span className="font-bold text-accent-success" data-id="element-2950">
                  {item.count} times
                </span>
              </div>)}
          </div>
        </Card>

        {/* Escalated Issues (Gap Detection) */}
        <Card className="p-6 border border-accent-warning/30 bg-accent-warning/5" data-id="element-2951">
          <h3 className="font-bold mb-4 flex items-center gap-2 text-accent-warning" data-id="element-2952">
            <AlertTriangle size={20} data-id="element-2953" /> Escalated Issues (Gap Detection)
          </h3>
          <p className="text-sm text-text-secondary mb-4" data-id="element-2954">
            Questions escalated because no documentation exists:
          </p>
          <div className="space-y-3 mb-6" data-id="element-2955">
            {[{
            q: '"Dostavljač se nije pojavio"',
            count: 5
          }, {
            q: '"Kupac ima alergijsku reakciju"',
            count: 3
          }, {
            q: '"Kasa je u minusu"',
            count: 3
          }].map((item, i) => <div key={i} className="flex justify-between items-center p-2 border-b border-accent-warning/20 last:border-0" data-id="element-2956">
                <span className="italic text-sm" data-id="element-2957">"{item.q}"</span>
                <span className="font-bold text-accent-warning" data-id="element-2958">
                  {item.count} times
                </span>
              </div>)}
          </div>
          <button className="w-full py-2 bg-accent-warning/10 text-accent-warning rounded-lg text-sm font-bold hover:bg-accent-warning/20 transition-colors" data-id="element-2959">
            Create Document from Issue
          </button>
        </Card>
      </div>

      {/* Usage Chart */}
      <Card className="p-6" data-id="element-2960">
        <h3 className="font-bold mb-4" data-id="element-2961">AI Usage by Hour</h3>
        <div className="h-64 w-full" data-id="element-2962">
          <ResponsiveContainer width="100%" height="100%" data-id="element-2963">
            <BarChart data={HOURLY_DATA} data-id="element-2964">
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-2965" />
              <XAxis dataKey="hour" stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} data-id="element-2966" />
              <Tooltip contentStyle={{
              backgroundColor: '#1A1F2E',
              borderColor: '#ffffff10',
              borderRadius: '8px'
            }} cursor={{
              fill: '#ffffff05'
            }} data-id="element-2967" />
              <Bar dataKey="chats" fill="#8B5CF6" radius={[4, 4, 0, 0]} data-id="element-2968" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Conversation Log */}
      <Card noPadding data-id="element-2969">
        <div className="p-4 border-b border-white/10 font-bold" data-id="element-2970">
          Recent Conversations
        </div>
        <div className="divide-y divide-white/5" data-id="element-2971">
          <div className="p-4 hover:bg-white/5 transition-colors" data-id="element-2972">
            <div className="flex justify-between items-start mb-2" data-id="element-2973">
              <div className="text-sm" data-id="element-2974">
                <span className="font-bold" data-id="element-2975">Marko S.</span> · Vračar ·{' '}
                <span className="text-text-secondary" data-id="element-2976">Dec 14, 14:32</span>
              </div>
              <div className="flex items-center gap-1 text-xs font-bold text-accent-success" data-id="element-2977">
                <CheckCircle size={12} data-id="element-2978" /> Resolved · 2 min
              </div>
            </div>
            <div className="space-y-1 text-sm" data-id="element-2979">
              <div className="flex gap-2" data-id="element-2980">
                <span className="text-text-secondary" data-id="element-2981">👤</span>
                <span className="italic" data-id="element-2982">"Gril temperatura pada"</span>
              </div>
              <div className="flex gap-2" data-id="element-2983">
                <span className="text-text-secondary" data-id="element-2984">🤖</span>
                <span data-id="element-2985">Suggested: gas check + thermostat reset</span>
              </div>
            </div>
          </div>

          <div className="p-4 hover:bg-white/5 transition-colors bg-accent-warning/5" data-id="element-2986">
            <div className="flex justify-between items-start mb-2" data-id="element-2987">
              <div className="text-sm" data-id="element-2988">
                <span className="font-bold" data-id="element-2989">Ana P.</span> · BB ·{' '}
                <span className="text-text-secondary" data-id="element-2990">Dec 14, 13:45</span>
              </div>
              <div className="flex items-center gap-1 text-xs font-bold text-accent-warning" data-id="element-2991">
                <AlertTriangle size={12} data-id="element-2992" /> Escalated to Jovana K.
              </div>
            </div>
            <div className="space-y-1 text-sm" data-id="element-2993">
              <div className="flex gap-2" data-id="element-2994">
                <span className="text-text-secondary" data-id="element-2995">👤</span>
                <span className="italic" data-id="element-2996">
                  "Kupac kaže da mu je pozlilo od hrane"
                </span>
              </div>
              <div className="flex gap-2" data-id="element-2997">
                <span className="text-text-secondary" data-id="element-2998">🤖</span>
                <span data-id="element-2999">Escalated: Health issue — beyond AI scope</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
}